// Growth engagement: one-time native share prompt + native in-app store review,
// both triggered after a "happy moment" (first export OR 3rd receipt processed).
// All native calls are guarded so they are safe no-ops on web/SSR.
import { Capacitor } from '@capacitor/core';
import { trackEvent } from './analytics';
import { metaShareApp } from './metaAppEvents';

const APP_STORE_URL = 'https://apps.apple.com/app/moneyfeed-ai/id6788915539';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.moneyfeed.app';
export const SHARE_MESSAGE =
  'I do my bookkeeping on WhatsApp with MoneyFeed — just chat or snap a receipt and the books are done. Try it free: ' +
  APP_STORE_URL;

const SHARE_PROMPT_KEY = 'mf_share_prompt_done';
const SESSION_SEEN_KEY = 'mf_session_started';
const HAPPY_EVENT = 'moneyfeed:happy-moment';

const isNative = () => {
  try { return Capacitor.isNativePlatform(); } catch { return false; }
};

const appVersion = () => {
  try { return String(process.env.REACT_APP_VERSION || window.__APP_VERSION__ || '1'); }
  catch { return '1'; }
};

const platform = () => {
  try { return Capacitor.getPlatform(); } catch { return 'web'; }
};

// The right store listing for the current platform (Play on Android, else App Store).
export const storeListingUrl = () => (platform() === 'android' ? PLAY_STORE_URL : APP_STORE_URL);

// Open the store listing directly — for the Settings "Rate MoneyFeed" button, i.e.
// users who dismissed the automatic in-app review prompt.
export function openStoreListing() {
  const url = storeListingUrl();
  trackEvent('rate_app_click', { platform: platform() });
  try {
    if (isNative()) { window.open(url, '_system'); return; }
  } catch { /* fall through */ }
  try { window.open(url, '_blank', 'noopener'); } catch { /* ignore */ }
}

// Mark that the current launch is an established session (not the very first one).
// Call once at startup. Review/share only fire on a later session.
export function markSession() {
  try {
    if (sessionStorage.getItem(SESSION_SEEN_KEY)) return;
    sessionStorage.setItem(SESSION_SEEN_KEY, '1');
    const count = Number(localStorage.getItem('mf_launch_count') || '0') + 1;
    localStorage.setItem('mf_launch_count', String(count));
  } catch { /* ignore */ }
}

const isFirstSession = () => {
  try { return Number(localStorage.getItem('mf_launch_count') || '0') <= 1; }
  catch { return false; }
};

// Request the native store-review dialog (SKStoreReviewController / Play In-App Review).
// Throttled to once per app version; never on the first session; native only.
export async function requestAppReview() {
  if (!isNative() || isFirstSession()) return;
  const key = `mf_review_${appVersion()}`;
  try {
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, String(Date.now()));
    const mod = await import('@capacitor-community/in-app-review');
    await mod.InAppReview.requestReview();
  } catch { /* system may ignore / plugin missing — stay silent */ }
}

// Share the app via the native share sheet (WhatsApp, Messages, etc).
// Web fallback: Web Share API, then clipboard.
export async function shareApp() {
  const done = (method) => {
    try { trackEvent('share_app', { method, platform: platform() }); } catch { /* ignore */ }
    try { metaShareApp(); } catch { /* ignore */ }
  };
  if (isNative()) {
    try {
      const mod = await import('@capacitor/share');
      await mod.Share.share({
        title: 'MoneyFeed',
        text: SHARE_MESSAGE,
        url: APP_STORE_URL,
        dialogTitle: 'Invite friends to MoneyFeed',
      });
      done('native_sheet');
      return true;
    } catch { return false; }
  }
  try {
    if (navigator.share) {
      await navigator.share({ title: 'MoneyFeed', text: SHARE_MESSAGE, url: APP_STORE_URL });
      done('web_share');
      return true;
    }
  } catch { return false; }
  try {
    await navigator.clipboard.writeText(SHARE_MESSAGE);
    done('clipboard');
    return 'copied';
  } catch { return false; }
}

const sharePromptDone = () => {
  try { return !!localStorage.getItem(SHARE_PROMPT_KEY); } catch { return false; }
};

export function dismissSharePrompt() {
  try { localStorage.setItem(SHARE_PROMPT_KEY, String(Date.now())); } catch { /* ignore */ }
}

// Fire when the user hits a positive milestone. Shows the share prompt once (ever)
// and asks for a native review (once per version). Never on the very first session.
export function markHappyMoment(reason = 'export') {
  if (isFirstSession()) return;
  // Native store review — fire in the background, system decides if it shows.
  requestAppReview();
  // One-time in-app share prompt (a dialog listens for this event).
  if (!sharePromptDone()) {
    try {
      window.dispatchEvent(new CustomEvent(HAPPY_EVENT, { detail: { reason } }));
    } catch { /* ignore */ }
  }
}

export { HAPPY_EVENT };
